import React, { useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";

export default function ColumnManagerDrawer({
  open,
  onClose,
  columns,            // DataGrid columns
  headerMap,          // { field: headerName }
  onApplyHeaderMap,   // (newMap) => void
}) {
  const [draft, setDraft] = useState({});

  // 打開時，把目前 headerMap 複製到 draft
  useEffect(() => {
    if (!open) return;
    const init = {};
    for (const c of columns) {
      init[c.field] = headerMap?.[c.field] ?? c.headerName ?? c.field;
    }
    setDraft(init);
  }, [open, columns, headerMap]);

  // 用來提示「哪些欄位會被合併」
  const mergeGroups = useMemo(() => {
    const g = new Map(); // displayName -> [field...]
    for (const [field, name] of Object.entries(draft)) {
      const key = (name || "").trim();
      if (!key) continue;
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(field);
    }
    // 只顯示會合併的（>=2）
    return Array.from(g.entries()).filter(([, fields]) => fields.length >= 2);
  }, [draft]);

  const handleApply = () => {
    // 你可以在這裡做 validation（例如空字串不允許）
    onApplyHeaderMap?.(draft);
    onClose?.();
  };

  const handleReset = () => {
    const init = {};
    for (const c of columns) {
      init[c.field] = c.headerName ?? c.field;
    }
    setDraft(init);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 420, p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Column Settings</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>

        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
          Rename columns. If multiple columns share the same name, they will be treated as a merge group.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ maxHeight: "60vh", overflow: "auto", pr: 1 }}>
          <Stack spacing={1.5}>
            {columns.map((c) => (
              <TextField
                key={c.field}
                size="small"
                label={c.field}
                value={draft[c.field] ?? ""}
                onChange={(e) => setDraft((prev) => ({ ...prev, [c.field]: e.target.value }))}
                helperText={c.field.startsWith("lab__") ? "Lab column" : "Base column"}
              />
            ))}
          </Stack>
        </Box>

        {mergeGroups.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">Merge preview</Typography>
            <Box sx={{ mt: 1, maxHeight: 140, overflow: "auto" }}>
              {mergeGroups.map(([name, fields]) => (
                <Typography key={name} variant="body2" sx={{ mb: 0.5 }}>
                  <b>{name}</b>  ← {fields.join(", ")}
                </Typography>
              ))}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" onClick={handleReset}>Reset</Button>
          <Button variant="contained" onClick={handleApply}>Apply</Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
